import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth, ROLES_FULL_ACCESS } from "../App";
import axios from "axios";
import { API } from "../App";
import UserFicheSheet from "../components/users/UserFicheSheet";
import OrgChart from "../components/users/OrgChart";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  Search,
  Users,
  Mail,
  Building2,
  Briefcase,
  Loader2,
  Download,
  RefreshCw,
  UserCheck,
  PowerOff,
  Trash2,
  List,
  Grid3X3,
  AlertTriangle,
} from "lucide-react";

// Microsoft icon
const MicrosoftIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const ROLE_CONFIG = {
  admin:         { label: "Admin",         dot: "bg-purple-500", badgeClass: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300" },
  hr:            { label: "RH",            dot: "bg-orange-400", badgeClass: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300" },
  manager:       { label: "Manager",       dot: "bg-[#1A4D2E]",  badgeClass: "bg-[#E8F5E9] text-[#1A4D2E] dark:bg-[#1A4D2E]/30 dark:text-[#4CAF70]" },
  collaborateur: { label: "Collaborateur", dot: "bg-gray-400",   badgeClass: "bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300" },
  employee:      { label: "Collaborateur", dot: "bg-gray-400",   badgeClass: "bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300" },
  comptabilité:  { label: "Comptabilité",  dot: "bg-blue-500",   badgeClass: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" },
};

const DEPT_OPTIONS = [
  "Expert métier",
  "Ressources Humaines",
  "Direction",
  "Technique",
];

const ROLE_OPTIONS = [
  { value: "admin",         label: "Admin"         },
  { value: "hr",            label: "RH"            },
  { value: "manager",       label: "Manager"       },
  { value: "collaborateur", label: "Collaborateur" },
  { value: "comptabilité",  label: "Comptabilité"  },
];

const getRoleBadge = (role) => {
  const config = ROLE_CONFIG[role] || ROLE_CONFIG.collaborateur;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#374151] dark:text-[#D6E4F5]">
      <span className={`w-2 h-2 rounded-full shrink-0 ${config.dot}`} />
      {config.label}
    </span>
  );
};

// ============== IMPORT DIALOG ==============

const ImportAzureDialog = ({ open, onClose, onImported }) => {
  const [azureUsers, setAzureUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [selected, setSelected] = useState({});
  const [roleAssign, setRoleAssign] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const fetchAzureUsers = useCallback(async () => {
    setIsLoading(true);
    setSelected({});
    setRoleAssign({});
    try {
      const response = await axios.get(`${API}/users/azure/list`);
      setAzureUsers(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors du chargement des utilisateurs Azure AD");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchAzureUsers();
    }
  }, [open, fetchAzureUsers]);

  const toggleSelect = (azureId) => {
    setSelected((prev) => ({ ...prev, [azureId]: !prev[azureId] }));
    if (!roleAssign[azureId]) {
      setRoleAssign((prev) => ({ ...prev, [azureId]: "collaborateur" }));
    }
  };

  const toggleAll = () => {
    const allSelected = filtered.every((u) => selected[u.azure_id]);
    const next = {};
    const nextRole = {};
    filtered.forEach((u) => {
      next[u.azure_id] = !allSelected;
      nextRole[u.azure_id] = roleAssign[u.azure_id] || "collaborateur";
    });
    setSelected((prev) => ({ ...prev, ...next }));
    setRoleAssign((prev) => ({ ...prev, ...nextRole }));
  };

  const handleImport = async () => {
    const toImport = azureUsers
      .filter((u) => selected[u.azure_id])
      .map((u) => ({
        azure_id: u.azure_id,
        email: u.email,
        first_name: u.first_name,
        last_name: u.last_name,
        role: roleAssign[u.azure_id] || "collaborateur",
        department: u.department || "",
        position: u.job_title || "",
      }));

    if (toImport.length === 0) {
      toast.warning("Sélectionnez au moins un utilisateur");
      return;
    }

    setIsImporting(true);
    try {
      const response = await axios.post(`${API}/users/azure/import`, toImport);
      toast.success(response.data.message);
      onImported();
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setIsImporting(false);
    }
  };

  const filtered = azureUsers.filter((u) => {
    const q = searchQuery.toLowerCase();
    return (
      u.display_name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.department?.toLowerCase().includes(q) ||
      u.job_title?.toLowerCase().includes(q)
    );
  });

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const allFilteredSelected = filtered.length > 0 && filtered.every((u) => selected[u.azure_id]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col dark:bg-[#0B1628] dark:border-[#1A3050]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-[#D6E4F5]">
            <MicrosoftIcon size={20} />
            Importer depuis Azure AD
          </DialogTitle>
          <DialogDescription className="dark:text-[#7EA8CE]">
            Sélectionnez les utilisateurs à importer et assignez-leur un rôle.
            Seuls les utilisateurs non encore présents dans WorkPulse sont affichés.
          </DialogDescription>
        </DialogHeader>

        {/* Search + Refresh */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={fetchAzureUsers}
            disabled={isLoading}
            className="dark:border-[#1A3050] dark:text-[#D6E4F5]"
            title="Rafraîchir"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
          </Button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto border rounded-lg dark:border-[#1A3050]">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-400 gap-2">
              <UserCheck className="w-10 h-10 opacity-40" />
              <p className="text-sm">
                {azureUsers.length === 0
                  ? "Aucun utilisateur Azure AD trouvé ou Azure AD non configuré"
                  : "Aucun résultat pour cette recherche"}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="dark:border-[#1A3050]">
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allFilteredSelected}
                      onCheckedChange={toggleAll}
                    />
                  </TableHead>
                  <TableHead className="dark:text-[#D6E4F5]">Utilisateur</TableHead>
                  <TableHead className="dark:text-[#D6E4F5]">Email</TableHead>
                  <TableHead className="dark:text-[#D6E4F5]">Département</TableHead>
                  <TableHead className="w-40 dark:text-[#D6E4F5]">Rôle à attribuer</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((u) => (
                  <TableRow
                    key={u.azure_id}
                    className={`cursor-pointer dark:border-[#1A3050] ${selected[u.azure_id] ? "bg-[#E8F5E9] dark:bg-emerald-900/20" : ""}`}
                    onClick={() => toggleSelect(u.azure_id)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={!!selected[u.azure_id]}
                        onCheckedChange={() => toggleSelect(u.azure_id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm text-[#111827] dark:text-[#D6E4F5]">
                          {u.display_name || `${u.first_name} ${u.last_name}`.trim() || u.email}
                        </p>
                        {u.job_title && (
                          <p className="text-xs text-gray-500 dark:text-[#7EA8CE]">{u.job_title}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600 dark:text-[#D6E4F5]">{u.email}</TableCell>
                    <TableCell className="text-sm text-gray-600 dark:text-[#D6E4F5]">{u.department || "-"}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Select
                        value={roleAssign[u.azure_id] || "collaborateur"}
                        onValueChange={(val) =>
                          setRoleAssign((prev) => ({ ...prev, [u.azure_id]: val }))
                        }
                        disabled={!selected[u.azure_id]}
                      >
                        <SelectTrigger className="h-8 text-xs dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTIONS.map((r) => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between gap-4">
          <p className="text-sm text-gray-500 dark:text-[#7EA8CE]">
            {selectedCount > 0
              ? `${selectedCount} utilisateur(s) sélectionné(s)`
              : `${filtered.length} utilisateur(s) disponible(s)`}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="dark:border-[#1A3050] dark:text-[#D6E4F5]">
              Annuler
            </Button>
            <Button
              onClick={handleImport}
              disabled={isImporting || selectedCount === 0}
              className="bg-[#1A4D2E] hover:bg-[#143D24] text-white"
            >
              {isImporting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Importer ({selectedCount})
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== MAIN PAGE ==============

const UsersPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDept, setFilterDept] = useState("all");
  const [viewMode, setViewMode] = useState("liste");
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [updatingRole, setUpdatingRole] = useState(null);
  const [togglingUser, setTogglingUser] = useState(null);
  const [deleteDialogUser, setDeleteDialogUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);

  const [selectedUserId, setSelectedUserId] = useState(null);

  const canManage = ROLES_FULL_ACCESS.includes(user?.role);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des collaborateurs");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  const handleRoleChange = async (userId, newRole) => {
    if (userId === user?.id) {
      toast.error("Impossible de modifier votre propre rôle");
      return;
    }
    setUpdatingRole(userId);
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole });
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u))
      );
      toast.success("Rôle mis à jour");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors de la mise à jour du rôle");
    } finally {
      setUpdatingRole(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialogUser) return;
    setDeletingUser(true);
    try {
      await axios.delete(`${API}/users/${deleteDialogUser.id}`);
      setUsers((prev) => prev.filter((u) => u.id !== deleteDialogUser.id));
      toast.success(`${deleteDialogUser.first_name} ${deleteDialogUser.last_name} a été supprimé`);
      setDeleteDialogUser(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeletingUser(false);
    }
  };

  const handleToggleActive = async (userId, currentlyActive) => {
    setTogglingUser(userId);
    try {
      const response = await axios.put(`${API}/users/${userId}/deactivate`);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, is_active: response.data.is_active } : u
        )
      );
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur");
    } finally {
      setTogglingUser(null);
    }
  };

  const filteredUsers = useMemo(() => users.filter((u) => {
    const query = searchQuery.toLowerCase();
    const matchSearch =
      u.first_name?.toLowerCase().includes(query) ||
      u.last_name?.toLowerCase().includes(query) ||
      u.email?.toLowerCase().includes(query) ||
      u.department?.toLowerCase().includes(query) ||
      u.position?.toLowerCase().includes(query);
    const matchDept = filterDept === "all" || u.department === filterDept;
    return matchSearch && matchDept;
  }), [users, searchQuery, filterDept]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-['Playfair_Display'] text-xl md:text-3xl font-bold text-[#111827] dark:text-[#D6E4F5]">
            Collaborateurs
          </h1>
          <p className="text-[#4B5563] dark:text-[#7EA8CE] mt-1">
            {filteredUsers.length} collaborateur{filteredUsers.length > 1 ? "s" : ""}
            {filteredUsers.length !== users.length && ` sur ${users.length}`}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <Input
              placeholder="Rechercher un collaborateur..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
              data-testid="search-users"
            />
          </div>

          {/* Toggle vue liste / trombinoscope */}
          <div className="flex rounded-lg border border-gray-200 dark:border-[#1A3050] overflow-hidden">
            <button
              onClick={() => setViewMode("liste")}
              className={`px-3 py-2 flex items-center gap-1.5 text-sm transition-colors ${
                viewMode === "liste"
                  ? "bg-[#1A4D2E] text-white"
                  : "bg-white dark:bg-[#0B1628] text-[#4B5563] dark:text-[#7EA8CE] hover:bg-gray-50 dark:hover:bg-[#112240]"
              }`}
              title="Vue liste"
            >
              <List className="w-4 h-4" />
              <span className="hidden sm:inline">Liste</span>
            </button>
            <button
              onClick={() => setViewMode("trombi")}
              className={`px-3 py-2 flex items-center gap-1.5 text-sm transition-colors border-l border-gray-200 dark:border-[#1A3050] ${
                viewMode === "trombi"
                  ? "bg-[#1A4D2E] text-white"
                  : "bg-white dark:bg-[#0B1628] text-[#4B5563] dark:text-[#7EA8CE] hover:bg-gray-50 dark:hover:bg-[#112240]"
              }`}
              title="Trombinoscope"
            >
              <Grid3X3 className="w-4 h-4" />
              <span className="hidden sm:inline">Trombi</span>
            </button>
          </div>

          {canManage && (
            <Button
              onClick={() => setImportDialogOpen(true)}
              className="bg-[#1A4D2E] hover:bg-[#143D24] text-white flex items-center gap-2"
              data-testid="import-azure-btn"
            >
              <MicrosoftIcon size={16} />
              <span className="hidden sm:inline">Importer depuis Azure AD</span>
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-neumorphic dark:bg-[#0B1628] dark:border-[#1A3050]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-[#E8F5E9] dark:bg-emerald-900/30">
              <Users className="w-6 h-6 text-[#1A4D2E] dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#111827] dark:text-[#D6E4F5]">{users.length}</p>
              <p className="text-sm text-[#4B5563] dark:text-[#7EA8CE]">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-neumorphic dark:bg-[#0B1628] dark:border-[#1A3050]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20">
              <Briefcase className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#111827] dark:text-[#D6E4F5]">
                {users.filter((u) => u.role === "manager").length}
              </p>
              <p className="text-sm text-[#4B5563] dark:text-[#7EA8CE]">Managers</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-neumorphic dark:bg-[#0B1628] dark:border-[#1A3050]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-purple-50 dark:bg-purple-900/20">
              <UserCheck className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#111827] dark:text-[#D6E4F5]">
                {users.filter((u) => ROLES_FULL_ACCESS.includes(u.role)).length}
              </p>
              <p className="text-sm text-[#4B5563] dark:text-[#7EA8CE]">Admin / RH</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-neumorphic dark:bg-[#0B1628] dark:border-[#1A3050]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-orange-50 dark:bg-orange-900/20">
              <Building2 className="w-6 h-6 text-[#E07A5F] dark:text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#111827] dark:text-[#D6E4F5]">
                {[...new Set(users.map((u) => u.department).filter(Boolean))].length}
              </p>
              <p className="text-sm text-[#4B5563] dark:text-[#7EA8CE]">Départements</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Vue trombi : organigramme sur mobile, grande grille sur desktop */}
      {viewMode === "trombi" && (
        <>
          {/* Mobile : organigramme */}
          <div className="sm:hidden border dark:border-[#1A3050] rounded-2xl bg-white dark:bg-[#0B1628] p-2">
            <OrgChart
              users={users}
              onOpen={(id) => setSelectedUserId(id)}
              canClick={true}
            />
          </div>

          {/* Desktop : grande grille */}
          {filteredUsers.length === 0 ? (
            <div className="hidden sm:flex text-center py-16 text-[#9CA3AF] dark:text-[#3D607E] flex-col items-center">
              <Users className="w-12 h-12 mb-3 opacity-50" />
              <p>Aucun collaborateur trouvé</p>
            </div>
          ) : (
            <div className="hidden sm:grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {filteredUsers.map((u) => {
                const rc = ROLE_CONFIG[u.role] || ROLE_CONFIG.collaborateur;
                return (
                  <div
                    key={u.id}
                    onClick={() => setSelectedUserId(u.id)}
                    className="flex flex-col items-center text-center p-4 rounded-2xl border dark:border-[#1A3050] bg-white dark:bg-[#0B1628] shadow-sm cursor-pointer hover:-translate-y-1 hover:shadow-md hover:border-[#1A4D2E]/40 dark:hover:border-[#1A4D2E]/60 hover:bg-[#F0FAF4] dark:hover:bg-[#112240] transition-all duration-200"
                  >
                    <Avatar className="w-16 h-16 mb-3 ring-2 ring-white dark:ring-[#0B1628] shadow-sm">
                      <AvatarImage src={u.avatar_url} className="object-cover" />
                      <AvatarFallback className="text-lg font-semibold bg-gradient-to-br from-[#1A4D2E] to-[#2D7A4E] text-white">
                        {getInitials(u.first_name, u.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <p className="font-semibold text-sm text-[#111827] dark:text-[#D6E4F5] leading-tight truncate w-full">
                      {u.first_name} {u.last_name}
                    </p>
                    {u.position && (
                      <p className="text-xs text-[#6B7280] dark:text-[#7EA8CE] mt-0.5 truncate w-full">{u.position}</p>
                    )}
                    {u.department && (
                      <p className="text-[10px] text-[#9CA3AF] dark:text-[#3D607E] mt-0.5 truncate w-full">{u.department}</p>
                    )}
                    <Badge className={`mt-2 text-[10px] px-2 py-0 border-0 ${rc.badgeClass}`}>{rc.label}</Badge>
                    {!u.is_active && (
                      <Badge className="mt-1 text-[10px] px-2 py-0 bg-gray-100 text-gray-500 dark:bg-gray-700/50 dark:text-gray-400 border-0">Inactif</Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Users Table (liste view) */}
      {viewMode === "liste" && (
      <Card className="card-neumorphic dark:bg-[#0B1628] dark:border-[#1A3050]">
        <CardContent className="p-0">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-[#9CA3AF]">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucun collaborateur trouvé</p>
              {canManage && (
                <Button
                  onClick={() => setImportDialogOpen(true)}
                  variant="outline"
                  className="mt-4"
                >
                  <MicrosoftIcon size={16} />
                  <span className="ml-2">Importer depuis Azure AD</span>
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Mobile : cartes */}
              <div className="md:hidden divide-y divide-gray-100 dark:divide-[#1A3050]">
                {filteredUsers.map((u) => (
                  <div
                    key={u.id}
                    className="p-4 flex items-start gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#112240]/50 transition-colors"
                    onClick={() => setSelectedUserId(u.id)}
                  >
                    <Avatar className="w-10 h-10 shrink-0">
                      <AvatarImage src={u.avatar_url} />
                      <AvatarFallback className="bg-[#E8F5E9] dark:bg-emerald-900/40 text-[#1A4D2E] dark:text-emerald-400 font-semibold text-sm">
                        {getInitials(u.first_name, u.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium text-[#111827] dark:text-[#D6E4F5] truncate">
                          {u.first_name} {u.last_name}
                        </p>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Badge className={
                            u.is_active
                              ? "bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800 text-xs"
                              : "bg-gray-100 text-gray-500 border border-gray-200 dark:bg-[#112240] dark:text-[#7EA8CE] dark:border-[#1A3050] text-xs"
                          }>
                            {u.is_active ? "Actif" : "Inactif"}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-xs text-[#4B5563] dark:text-[#7EA8CE] truncate mt-0.5">{u.email}</p>
                      {(u.department || u.position) && (
                        <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] mt-0.5">
                          {[u.position, u.department].filter(Boolean).join(" · ")}
                        </p>
                      )}
                      {canManage && u.id !== user?.id && (
                        <div className="mt-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={u.role}
                            onValueChange={(val) => handleRoleChange(u.id, val)}
                            disabled={updatingRole === u.id}
                          >
                            <SelectTrigger className="h-7 w-36 border border-gray-200 dark:border-[#1A3050] bg-white dark:bg-[#112240] text-xs rounded-md px-2 focus:ring-0">
                              <SelectValue>{getRoleBadge(u.role)}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {ROLE_OPTIONS.map((r) => (
                                <SelectItem key={r.value} value={r.value} className="text-xs">
                                  {getRoleBadge(r.value)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => handleToggleActive(u.id, u.is_active)}
                            disabled={togglingUser === u.id}
                            title={u.is_active ? "Désactiver" : "Activer"}
                            className={`h-7 w-7 ${
                              u.is_active
                                ? "text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                                : "text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                            }`}
                          >
                            {togglingUser === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PowerOff className="w-3.5 h-3.5" />}
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => setDeleteDialogUser(u)}
                            title="Supprimer définitivement"
                            className="h-7 w-7 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop : tableau */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="dark:border-[#1A3050]">
                      <TableHead className="dark:text-[#7EA8CE]">Collaborateur</TableHead>
                      <TableHead className="dark:text-[#7EA8CE]">Email</TableHead>
                      <TableHead className="dark:text-[#7EA8CE]">Département</TableHead>
                      <TableHead className="dark:text-[#7EA8CE]">Poste</TableHead>
                      <TableHead className="dark:text-[#7EA8CE]">Rôle</TableHead>
                      <TableHead className="dark:text-[#7EA8CE]">Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((u) => (
                      <TableRow
                        key={u.id}
                        className="dark:border-[#1A3050] cursor-pointer hover:bg-gray-50 dark:hover:bg-[#112240]/70"
                        onClick={() => setSelectedUserId(u.id)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="w-9 h-9">
                              <AvatarImage src={u.avatar_url} />
                              <AvatarFallback className="bg-[#E8F5E9] dark:bg-emerald-900/40 text-[#1A4D2E] dark:text-emerald-400 font-semibold text-sm">
                                {getInitials(u.first_name, u.last_name)}
                              </AvatarFallback>
                            </Avatar>
                            <p className="font-medium text-[#111827] dark:text-[#D6E4F5]">{u.first_name} {u.last_name}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-[#4B5563] dark:text-[#7EA8CE]">
                            <Mail className="w-4 h-4 shrink-0" />
                            <span className="truncate max-w-[180px]">{u.email}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-[#4B5563] dark:text-[#7EA8CE]">{u.department || "-"}</TableCell>
                        <TableCell className="text-[#4B5563] dark:text-[#7EA8CE]">{u.position || "-"}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {canManage && u.id !== user?.id ? (
                            <div className="relative flex items-center gap-1">
                              {updatingRole === u.id && (
                                <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                              )}
                              <Select value={u.role} onValueChange={(val) => handleRoleChange(u.id, val)} disabled={updatingRole === u.id}>
                                <SelectTrigger className="h-8 w-38 border-0 shadow-none bg-transparent dark:bg-transparent text-xs font-medium text-[#374151] dark:text-[#D6E4F5] hover:bg-gray-100 dark:hover:bg-[#112240] rounded-md px-2 focus:ring-0">
                                  <SelectValue>{getRoleBadge(u.role)}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  {ROLE_OPTIONS.map((r) => (
                                    <SelectItem key={r.value} value={r.value} className="text-xs">
                                      {getRoleBadge(r.value)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          ) : (
                            getRoleBadge(u.role)
                          )}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            <Badge className={
                              u.is_active
                                ? "bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800"
                                : "bg-gray-100 text-gray-500 border border-gray-200 dark:bg-[#112240] dark:text-[#7EA8CE] dark:border-[#1A3050]"
                            }>
                              {u.is_active ? "Actif" : "Inactif"}
                            </Badge>
                            {canManage && u.id !== user?.id && (
                              <>
                                <Button
                                  variant="ghost" size="icon"
                                  onClick={() => handleToggleActive(u.id, u.is_active)}
                                  disabled={togglingUser === u.id}
                                  title={u.is_active ? "Désactiver" : "Activer"}
                                  className={`h-7 w-7 ${
                                    u.is_active
                                      ? "text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                                      : "text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                                  }`}
                                >
                                  {togglingUser === u.id
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <PowerOff className="w-3.5 h-3.5" />}
                                </Button>
                                <Button
                                  variant="ghost" size="icon"
                                  onClick={() => setDeleteDialogUser(u)}
                                  title="Supprimer définitivement"
                                  className="h-7 w-7 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialogUser} onOpenChange={() => setDeleteDialogUser(null)}>
        <DialogContent className="max-w-md dark:bg-[#0B1628] dark:border-[#1A3050]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Supprimer l'utilisateur
            </DialogTitle>
            <DialogDescription className="dark:text-[#7EA8CE]">
              Vous êtes sur le point de supprimer définitivement{" "}
              <span className="font-semibold text-[#111827] dark:text-[#D6E4F5]">
                {deleteDialogUser?.first_name} {deleteDialogUser?.last_name}
              </span>
              . Cette action est <span className="font-semibold text-red-600 dark:text-red-400">irréversible</span> et supprimera toutes les données associées.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogUser(null)}
              disabled={deletingUser}
              className="dark:border-[#1A3050] dark:text-[#D6E4F5]"
            >
              Annuler
            </Button>
            <Button
              onClick={handleDeleteUser}
              disabled={deletingUser}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deletingUser ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <ImportAzureDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImported={fetchUsers}
      />

      {/* Collaborateur Fiche */}
      <UserFicheSheet
        userId={selectedUserId}
        open={!!selectedUserId}
        onClose={() => setSelectedUserId(null)}
        allUsers={users}
        onRefresh={fetchUsers}
        onOpenUser={(id) => setSelectedUserId(id)}
      />
    </div>
  );
};

export default UsersPage;
